import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBroadcastGroups, getBroadcastFilterOptions, createBroadcast, cancelBroadcastGroup, updateBroadcastGroup } from '@/services/api';
import { checkBroadcastEditable } from './utils/broadcastUtils';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import BroadcastHeader from './components/BroadcastHeader';
import BroadcastStats from './components/BroadcastStats';
import BroadcastFiltersComponent from './components/BroadcastFilters';
import BroadcastGroupGrid from './components/BroadcastGroupGrid';
import { ViewBroadcastGroupModal } from './components/ViewBroadcastGroupModal';
import { BroadcastFilters, BroadcastFormData } from './types/broadcast.types';
import {
    sortGroupBroadcasts,
    filterGroupBroadcasts,
    getGroupStatus,
    getGroupTotalSent,
    getGroupSuccessRate,
} from './utils/groupBroadcastUtils';
import { Loader2, MessageSquare } from 'lucide-react';
import CreateBroadcastModal from './components/CreateBroadcastModal';
import type { BroadcastGroupSummary } from '@/types/broadcast';

type PageOption = { id: string | number; name: string; page_id: string }

/** Retorna a data de ontem em YYYY-MM-DD (timezone local do browser). */
const getYesterdayDateStr = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export default function Broadcasts() {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isViewGroupModalOpen, setIsViewGroupModalOpen] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const perPage = 10;
    const [filters, setFilters] = useState<BroadcastFilters>({
        sortBy: 'created_at',
        sortOrder: 'desc',
        // Default: a partir de ontem (cobre ontem + hoje + agendados futuros)
        dateFrom: getYesterdayDateStr(),
    });

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Query para listar broadcasts a nível de grupo (paginado)
    // Filtros server-side: categoryId, metaUserName, projectId, dateFrom.
    // Mudança em qualquer um invalida cache e dispara nova query.
    const { data: paginatedData, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: [
            'broadcast-groups', page, perPage,
            filters.dateFrom, filters.categoryId, filters.metaUserName, filters.projectId,
        ],
        queryFn: () => getBroadcastGroups(
            page, perPage, filters.dateFrom,
            {
                categoryId: filters.categoryId,
                metaUserName: filters.metaUserName,
                projectId: filters.projectId,
            },
        ),
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 60,
        refetchOnWindowFocus: false,
    });

    // Query separada pras opções dos dropdowns. TTL 5min porque categorias /
    // contas Meta / projetos quase nunca mudam (vs. broadcasts em si).
    const { data: filterOptions } = useQuery({
        queryKey: ['broadcast-filter-options'],
        queryFn: getBroadcastFilterOptions,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const groups = useMemo((): BroadcastGroupSummary[] => paginatedData?.items ?? [], [paginatedData]);
    const totalPages = paginatedData?.pages ?? 1;
    const totalGroups = paginatedData?.total ?? 0;

    const pages = useMemo<PageOption[]>(() => {
        if (!groups?.length) return [];
        const unique = new Map<string, PageOption>();
        for (const g of groups) {
            for (const p of g.pages ?? []) {
                const id = String(p.page_id ?? '').trim();
                const name = String(p.page_name ?? '').trim();
                if (!id || !name) continue;
                if (!unique.has(id)) unique.set(id, { id: p.id ?? id, name, page_id: id });
            }
        }
        return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [groups]);

    const availableLanguages = useMemo(() => {
        const langs = new Set<string>();
        for (const g of groups) {
            if (g.ai_translate && g.target_language) langs.add(g.target_language);
        }
        return Array.from(langs).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [groups]);

    const categories = useMemo(() => filterOptions?.categories ?? [], [filterOptions]);
    const metaUsers = useMemo(() => filterOptions?.meta_users ?? [], [filterOptions]);
    const projects = useMemo(() => filterOptions?.projects ?? [], [filterOptions]);

    const filteredAndSortedGroups = useMemo(() => {
        let result = filterGroupBroadcasts(groups, filters);
        const sortBy: 'created_at' | 'total_sent' | 'success_rate' =
            filters.sortBy === 'sent_count' ? 'total_sent' :
                filters.sortBy === 'success_rate' ? 'success_rate' : 'created_at';
        result = sortGroupBroadcasts(result, sortBy, filters.sortOrder || 'desc');
        return result;
    }, [groups, filters]);

    const stats = useMemo(() => {
        const totalSent = groups.reduce((sum, g) => sum + getGroupTotalSent(g), 0);
        const finishedGroups = groups.filter(g => {
            const s = getGroupStatus(g);
            return s === 'sent' || s === 'partial';
        });
        const averageSuccessRate = finishedGroups.length > 0
            ? finishedGroups.reduce((sum, g) => sum + getGroupSuccessRate(g), 0) / finishedGroups.length
            : 0;
        const pendingCount = groups.filter(g => ['pending', 'processing'].includes(getGroupStatus(g))).length;

        return {
            totalBroadcasts: totalGroups,
            totalSent,
            averageSuccessRate,
            pendingCount
        };
    }, [groups, totalGroups]);

    const [duplicateInitialData, setDuplicateInitialData] = useState<BroadcastFormData | undefined>(undefined);
    const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
    const [groupToCancel, setGroupToCancel] = useState<BroadcastGroupSummary | null>(null);

    const handleCreateClick = () => {
        setDuplicateInitialData(undefined);
        setEditingGroupId(null);
        setIsCreateModalOpen(true);
    };

    const handleEditGroup = (group: BroadcastGroupSummary) => {
        // Re-check da janela editavel no momento do clique (defesa em profundidade)
        const check = checkBroadcastEditable({
            status: group.status,
            is_scheduled: !!group.scheduled_at,
            scheduled_at: group.scheduled_at,
        });
        if (!check.editable) {
            toast({
                variant: 'destructive',
                title: 'Edição bloqueada',
                description: check.reason || 'Não é possível editar agora.',
            });
            return;
        }
        const uniquePages = (group.pages ?? []).map(p => ({
            id: p.id,
            page_id: p.page_id,
            page_name: p.page_name,
        }));
        const prefill: BroadcastFormData = {
            title: group.title || group.name,
            selectedPages: uniquePages,
            blocks: group.template_params?.blocks ?? [],
            isScheduled: !!group.scheduled_at,
            // scheduled_at vem como ISO "+00:00" — converter pra "yyyy-MM-ddTHH:mm" (wall clock)
            scheduledTime: group.scheduled_at
                ? group.scheduled_at.slice(0, 16).replace(' ', 'T')
                : '',
            broadcastType: !group.message_tag
                ? 'standard'
                : group.message_tag === 'ACCOUNT_UPDATE' || group.message_tag === 'CONFIRMED_EVENT_UPDATE'
                    ? 'utility'
                    : 'message_tag',
            messageTag: group.message_tag ?? null,
            aiTranslate: !!group.ai_translate,
            targetLanguage: group.target_language,
            customTemplateId: (group as any).custom_template_id ?? null,
            urlOverride: (group as any).url_override ?? null,
        };
        setDuplicateInitialData(prefill);
        setEditingGroupId(group.group_broadcast_id);
        setIsCreateModalOpen(true);
    };

    const updateGroupMutation = useMutation({
        mutationFn: (params: { groupId: number; data: BroadcastFormData }) => {
            const blocks = params.data.blocks ?? [];
            return updateBroadcastGroup(params.groupId, {
                title: params.data.title || undefined,
                blocks,
                is_scheduled: params.data.isScheduled,
                scheduled_at: params.data.isScheduled ? params.data.scheduledTime : undefined,
            } as any);
        },
        onSuccess: (data) => {
            toast({
                title: 'Broadcast atualizado',
                description: `${data.updated_count} envios atualizados.`,
            });
            setEditingGroupId(null);
            setDuplicateInitialData(undefined);
            setIsCreateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
        },
        onError: (err: any) => {
            const detail = err?.response?.data?.detail || err?.message || 'Erro ao atualizar';
            toast({ variant: 'destructive', title: 'Erro', description: detail });
        },
    });

    // TODO: rota desabilitada temporariamente
    // const handleViewGroup = (group: BroadcastGroupSummary) => {
    //     setSelectedGroupId(group.group_broadcast_id);
    //     setIsViewGroupModalOpen(true);
    // };

    const handleDuplicate = (group: BroadcastGroupSummary) => {
        const uniquePages = (group.pages ?? []).map(p => ({
            id: p.id,
            page_id: p.page_id,
            page_name: p.page_name,
        }));
        const prefill: BroadcastFormData = {
            title: (group.title || group.name) ? `Cópia de ${group.title || group.name}` : undefined,
            selectedPages: uniquePages,
            blocks: group.template_params?.blocks ?? [],
            isScheduled: false,
            scheduledTime: '',
            // Deriva o tipo a partir do message_tag salvo:
            //   null                                        → standard
            //   ACCOUNT_UPDATE | CONFIRMED_EVENT_UPDATE     → utility (CEU mantido por retrocompat)
            //   qualquer outro                              → message_tag (custom)
            broadcastType: !group.message_tag
                ? 'standard'
                : group.message_tag === 'ACCOUNT_UPDATE' || group.message_tag === 'CONFIRMED_EVENT_UPDATE'
                    ? 'utility'
                    : 'message_tag',
            messageTag: group.message_tag ?? null,
            aiTranslate: !!group.ai_translate,
            targetLanguage: group.target_language,
            customTemplateId: (group as any).custom_template_id ?? null,
            urlOverride: (group as any).url_override ?? null,
        };
        setDuplicateInitialData(prefill);
        setIsCreateModalOpen(true);
    };

    const handleCreateBroadcast = (data: BroadcastFormData) => {
        // Modo edição — atualiza grupo existente
        if (editingGroupId) {
            updateGroupMutation.mutate({ groupId: editingGroupId, data });
            return;
        }
        // Modo criação
        const pageIds = data.selectedPages.map(p => String(p.page_id)).filter(Boolean);
        if (pageIds.length === 0) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Selecione pelo menos uma página.' });
            return;
        }
        // Captura o fuso do browser pra que o backend converta `scheduled_at`
        // (wall-clock que o user digitou) → UTC corretamente. Sem isso,
        // o backend cai num fallback e pode disparar no horário errado.
        const clientTz = (() => {
            try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
                return undefined;
            }
        })();
        createBroadcastMutation.mutate({
            page_ids: pageIds,
            title: data.title || undefined,
            blocks: data.blocks,
            is_scheduled: data.isScheduled,
            scheduled_at: data.isScheduled ? data.scheduledTime : undefined,
            message_tag: data.messageTag ?? undefined,
            ai_translate: data.aiTranslate,
            target_language: data.targetLanguage,
            ai_provider: data.aiProvider,
            include_inactive: data.includeInactive,
            audience_window: data.audienceWindow ?? 'all',
            client_tz: clientTz,
            custom_template_id: data.customTemplateId ?? undefined,
            url_override: data.urlOverride ?? undefined,
        });
    };

    const cancelGroupMutation = useMutation({
        mutationFn: (groupId: number) => cancelBroadcastGroup(groupId),
        onSuccess: (data) => {
            const cancelledCount = data.cancelled.length;
            const sendsCancelled = data.total_sends_cancelled;
            const skipped = data.skipped.length;
            toast({
                title: 'Grupo cancelado',
                description: `${cancelledCount} broadcast(s) cancelado(s), ${sendsCancelled.toLocaleString('pt-BR')} envios bloqueados${skipped > 0 ? ` (${skipped} já finalizado(s))` : ''}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
            setGroupToCancel(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao cancelar grupo',
                description: error.response?.data?.detail || error.response?.data?.message || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para criar broadcast
    const createBroadcastMutation = useMutation({
        mutationFn: (data: Parameters<typeof createBroadcast>[0]) => createBroadcast(data),
        onSuccess: (_, variables) => {
            const description = variables.ai_translate
                ? 'Broadcast criado! A tradução automática está sendo gerada em background.'
                : 'O broadcast foi criado com sucesso.';
            toast({ title: 'Broadcast criado!', description });
            setPage(1);
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
            setIsCreateModalOpen(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar broadcast',
                description: error.response?.data?.detail || error.response?.data?.message || 'Ocorreu um erro.',
            });
        },
    });

    return (
        <div className="space-y-6">
            <BroadcastHeader
                onCreateClick={handleCreateClick}
                onRefresh={() => {
                    queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
                    refetch();
                }}
                isRefreshing={isFetching}
            />

            {isLoading && (
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-[#0f172a]" />
                    <p className="ml-3 text-gray-500">Carregando broadcasts...</p>
                </div>
            )}

            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium">Erro ao carregar broadcasts. Tente novamente.</p>
                </div>
            )}

            {!isLoading && !error && groups.length > 0 && (
                <BroadcastStats
                    totalBroadcasts={stats.totalBroadcasts}
                    totalSent={stats.totalSent}
                    averageSuccessRate={stats.averageSuccessRate}
                    pendingCount={stats.pendingCount}
                />
            )}

            {/* Mostra filtros quando há resultados OU quando um filtro está ativo (pra
                permitir que o user limpe o filtro e veja tudo). */}
            {!isLoading && !error && (groups.length > 0 || !!filters.dateFrom || !!filters.categoryId || !!filters.metaUserName || !!filters.projectId) && (
                <BroadcastFiltersComponent
                    pages={pages}
                    categories={categories}
                    metaUsers={metaUsers}
                    projects={projects}
                    filters={filters}
                    onFiltersChange={(f) => { setFilters(f); setPage(1); }}
                    availableLanguages={availableLanguages}
                />
            )}

            {!isLoading && !error && filteredAndSortedGroups.length > 0 ? (
                <BroadcastGroupGrid
                    groups={filteredAndSortedGroups}
                    onView={(group) => { setSelectedGroupId(group.group_broadcast_id); setIsViewGroupModalOpen(true); }}
                    onEdit={handleEditGroup}
                    onDuplicate={handleDuplicate}
                    onCancel={setGroupToCancel}
                />
            ) : !isLoading && !error && groups.length > 0 ? (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-[#0f172a]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum broadcast encontrado</h3>
                    <p className="text-gray-600 mb-6">Tente ajustar os filtros para ver mais resultados.</p>
                </div>
            ) : null}

            {!isLoading && !error && groups.length === 0 && totalGroups === 0 && (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-[#0f172a]" />
                    </div>
                    {filters.dateFrom ? (
                        <>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum broadcast no período</h3>
                            <p className="text-gray-600 mb-6">Não há broadcasts a partir de {filters.dateFrom}. Tente uma data anterior.</p>
                            <Button
                                variant="outline"
                                onClick={() => { setFilters({ ...filters, dateFrom: undefined }); setPage(1); }}
                                className="rounded-xl"
                            >
                                Ver todos os broadcasts
                            </Button>
                        </>
                    ) : (
                        <>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum broadcast cadastrado</h3>
                            <p className="text-gray-600 mb-6">Crie seu primeiro broadcast para começar.</p>
                        </>
                    )}
                </div>
            )}

            {/* Paginação */}
            {!isLoading && !error && totalPages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                    <span className="text-sm text-gray-400">{page} de {totalPages}</span>
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
                </div>
            )}

            <CreateBroadcastModal
                open={isCreateModalOpen}
                onOpenChange={(open) => {
                    setIsCreateModalOpen(open);
                    if (!open) {
                        setEditingGroupId(null);
                        setDuplicateInitialData(undefined);
                    }
                }}
                onSubmit={handleCreateBroadcast}
                isLoading={createBroadcastMutation.isPending || updateGroupMutation.isPending}
                initialData={duplicateInitialData}
                isEditMode={editingGroupId !== null}
            />

            <ConfirmDialog
                open={!!groupToCancel}
                onOpenChange={(open) => { if (!open) setGroupToCancel(null); }}
                title="Cancelar grupo de broadcast?"
                description={
                    groupToCancel
                        ? `Todos os broadcasts pendentes do grupo "${groupToCancel.title || groupToCancel.name || `#${groupToCancel.group_broadcast_id}`}" serão cancelados. Broadcasts já enviados não serão afetados.`
                        : undefined
                }
                confirmLabel={cancelGroupMutation.isPending ? 'Cancelando...' : 'Cancelar grupo'}
                cancelLabel="Voltar"
                variant="danger"
                isLoading={cancelGroupMutation.isPending}
                onConfirm={() => groupToCancel && cancelGroupMutation.mutate(groupToCancel.group_broadcast_id)}
            />
            <ViewBroadcastGroupModal
                open={isViewGroupModalOpen}
                onOpenChange={setIsViewGroupModalOpen}
                groupBroadcastId={selectedGroupId}
            />
        </div>
    );
}
